/**
 * /earthworks/inventory — the materials a tenant stocks (GET /earthworks/materials, EW-9).
 *
 * Full width. Filters in the URL: search, category, low stock. Each item opens
 * its own page. On hand is the API's sum of moves; low and below-zero items
 * are flagged and listed in amber — a warning, never a block.
 *
 * Prices (cost, sell price, stock value) are drawn only for the roles that read
 * the rate card; for anyone else the API sends none and no price column exists.
 * A field hand sees names and quantities.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, MapPin, Package, Plus } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useEwMaterials } from '../../../hooks/useEwInventory';
import { queryLoadFailure } from '../../../lib/load-failure';
import { canAdminEw, canReadEwRates, catalogLabel, fmtMoney } from '../../../lib/earthworks';
import type { EwMaterial } from '../../../types/earthworks';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useOpenRecord } from '../../downhole/recordPage';
import { EW_EYEBROW, EW_INPUT, EwBadge, EwChip, EwChipRow, EwEmpty, EwPanel, EwRegister, type EwColumn } from '../EwBits';
import { EW_STOCK_SKIN, EwStockWarnings, ewQty, stockState } from './inventoryBits';

export default function EwInventoryScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canSeePrices = canReadEwRates(user?.role);
  const canWrite = canAdminEw(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const category = searchParams.get('category') ?? '';
  const lowOnly = searchParams.get('low') === '1';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  const listQuery = useEwMaterials({ q, category, lowStock: lowOnly });
  const allQuery = useEwMaterials();
  const items = listQuery.data?.data ?? [];
  const options = allQuery.data?.options ?? listQuery.data?.options;
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (m: EwMaterial) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const warnings = allQuery.data?.warnings ?? [];
  const filtered = !!(q || category || lowOnly);
  const unitOf = (m: EwMaterial) => m.unitLabel || catalogLabel(options?.units, m.unit);

  const columns: EwColumn<EwMaterial>[] = [
    { key: 'name', header: 'Item', render: (m) => (<><p className="font-semibold text-white">{m.name}</p><p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{m.code || '—'}</p></>) },
    { key: 'category', header: 'Category', render: (m) => m.categoryLabel || catalogLabel(options?.categories, m.category) },
    { key: 'onHand', header: 'On hand', align: 'right', render: (m) => <span className="font-mono">{ewQty(m.onHand, unitOf(m))}</span> },
    { key: 'reorder', header: 'Reorder at', align: 'right', render: (m) => <span className="font-mono">{ewQty(m.lowStockThreshold, unitOf(m))}</span> },
    { key: 'state', header: 'Stock', render: (m) => <EwBadge skin={EW_STOCK_SKIN[stockState(m)]} value={stockState(m)} /> },
    { key: 'home', header: 'Home location', render: (m) => m.locationName || '—' },
    ...(canSeePrices
      ? ([
          { key: 'cost', header: 'Cost / unit', align: 'right', render: (m) => <span className="font-mono">{fmtMoney(m.costPerUnit, m.currency)}</span> },
          { key: 'sell', header: 'Sell / unit', align: 'right', render: (m) => <span className="font-mono">{fmtMoney(m.sellPrice, m.currency)}</span> },
          { key: 'value', header: 'Stock value', align: 'right', render: (m) => <span className="font-mono">{fmtMoney(m.stockValue, m.currency)}</span> },
        ] as EwColumn<EwMaterial>[])
      : []),
  ];

  return (
    <div data-testid="ew-inventory-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title="Inventory"
        description="Materials the company stocks — gravel, fill, sand, topsoil, fabric, culverts, piles — what is on hand, and every move in and out."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Package className="size-4" aria-hidden="true" />, label: 'Items', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Low stock', tone: 'accent', loading: allQuery.isLoading, value: countOf((m) => m.lowStock && !m.negative) },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Below zero', tone: 'accent', loading: allQuery.isLoading, value: countOf((m) => m.negative) },
        ]}
        actions={
          <div className="flex flex-wrap gap-2">
            <button type="button" className={DH_BAND_SECONDARY} onClick={() => navigate('/earthworks/inventory/locations')}>
              <MapPin className="size-4" aria-hidden="true" />
              Locations
            </button>
            {canWrite && (
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => navigate('/earthworks/inventory/new')}>
                <Plus className="size-4" aria-hidden="true" />
                New item
              </button>
            )}
          </div>
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <EwStockWarnings messages={warnings.map((w) => w.message)} lead="Stock to look at" />
          <EwChipRow label="Filter by stock">
            <EwChip label="All items" active={!lowOnly} onClick={() => setParam('low', '')} />
            <EwChip label="Low or below zero" active={lowOnly} onClick={() => setParam('low', '1')} count={allQuery.isLoading ? undefined : countOf((m) => m.lowStock || m.negative)} />
          </EwChipRow>
          <div className="grid gap-3 sm:grid-cols-[20rem_16rem]">
            <input className={EW_INPUT} aria-label="Search items" placeholder="Search by name or code" value={q} onChange={(e) => setParam('q', e.target.value)} />
            <select className={EW_INPUT} aria-label="Filter by category" value={category} onChange={(e) => setParam('category', e.target.value)}>
              <option value="">Every category</option>
              {(options?.categories ?? []).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <EwPanel eyebrow="Inventory" title="Items" testId="ew-material-register" footer={!listQuery.isLoading && items.length ? `${items.length} item${items.length === 1 ? '' : 's'}` : undefined}>
            <EwRegister
              query={listQuery}
              rows={items}
              columns={columns}
              rail={(m) => EW_STOCK_SKIN[stockState(m)].rail}
              onOpen={(m) => openRecord(`/earthworks/inventory/${m.id}`)}
              subject="inventory"
              testId="ew-material"
              minWidth={canSeePrices ? 'min-w-[1100px]' : 'min-w-[800px]'}
              empty={filtered ? <EwEmpty>No item matches these filters.</EwEmpty> : <EwEmpty>No items yet. Add the materials the company keeps in stock.</EwEmpty>}
              card={(m) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-white">{m.name}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{m.categoryLabel || '—'} · {m.locationName || 'No home location'}</p>
                    </div>
                    <span className="font-mono text-sm text-white">{ewQty(m.onHand, unitOf(m))}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs text-fieldiq-text">
                    <EwBadge skin={EW_STOCK_SKIN[stockState(m)]} value={stockState(m)} />
                    {canSeePrices && <span className="font-mono">{fmtMoney(m.sellPrice, m.currency)} sell</span>}
                  </div>
                </>
              )}
            />
          </EwPanel>
        </div>
      </section>
    </div>
  );
}
